import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardActions,
  Stack,
  Breadcrumbs,
  Link,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSnackbar } from "notistack";
import { getAnimalById } from "../../api/animal";
import { getTreatmentsByAnimalId } from "../../api/treatment";
import { apiUrl } from "../../api/apiClient";
import { APP_HOME_PATH } from "../../config/routes";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard from "../../components/MobileListCard";
import {
  uploadAnimalImages,
  getAnimalImages,
  deleteAnimalImage,
} from "../../api/image";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import LivestockIcon from "../../components/icons/LivestockIcon";
import AnimalFamilyTree from "../../components/animal/AnimalFamilyTree";
import AnimalMilking from "../../components/animal/AnimalMilking";

const TAB_OVERVIEW = 0;
const TAB_FAMILY = 1;
const TAB_TREATMENTS = 2;
const TAB_MILKING = 3;
const CHART_COLORS = ["#1565c0", "#00897b", "#ef6c00", "#7b1fa2", "#2e7d32", "#6d4c41"];

const statusChipSx = (status) => {
  const s = (status || "").toLowerCase().replace(/\s+/g, "_");
  if (s.includes("farm") || s === "on_farm")
    return { color: "success", variant: "filled" };
  if (s.includes("sold")) return { color: "warning", variant: "filled" };
  if (s.includes("deceas")) return { color: "error", variant: "filled" };
  if (s.includes("transfer"))
    return { color: "info", variant: "filled" };
  return { variant: "outlined" };
};

const AnimalProfile = () => {
  const theme = useTheme();
  const narrowTabs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileList = useIsMobileList("md");
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const farmTenantIdRaw = searchParams.get("tenant_id");
  const farmTenantIdNum =
    farmTenantIdRaw != null && farmTenantIdRaw !== ""
      ? Number(farmTenantIdRaw)
      : null;
  const farmTenantId =
    farmTenantIdNum != null && Number.isFinite(farmTenantIdNum)
      ? farmTenantIdNum
      : null;
  const profileQs =
    farmTenantId != null ? `?tenant_id=${farmTenantId}` : "";
  const [animal, setAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [dam, setDam] = useState(null);
  const [sire, setSire] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState("");
  const [treatmentSearch, setTreatmentSearch] = useState("");

  const animalsListHref = `/animals${profileQs}`;
  const createAnimalHref = `/animal/create_animal${profileQs}`;
  const editAnimalHref = id ? `/animal/${id}/edit${profileQs}` : "#";
  const addTreatmentHref = useMemo(() => {
    const params = new URLSearchParams();
    if (id) params.set("animal_id", String(id));
    if (farmTenantId != null) params.set("tenant_id", String(farmTenantId));
    const qs = params.toString();
    return qs ? `/treatment/create_treatment?${qs}` : "/treatment/create_treatment";
  }, [id, farmTenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [animalData, imagesData] = await Promise.all([
          getAnimalById(id, farmTenantId),
          getAnimalImages(id),
        ]);
        setAnimal(animalData);
        setImages(imagesData.images || []);
      } catch (err) {
        setError(err.message || "Failed to fetch animal details");
        enqueueSnackbar("Failed to load animal details", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, farmTenantId, enqueueSnackbar]);

  useEffect(() => {
    let abort = false;

    const loadParents = async () => {
      // if we don’t yet have the animal, or no parent IDs, clear and return
      if (!animal) {
        setDam(null);
        setSire(null);
        return;
      }

      try {
        const damReq = animal.dam_id
          ? getAnimalById(animal.dam_id, farmTenantId)
          : Promise.resolve(null);
        const sireReq = animal.sire_id
          ? getAnimalById(animal.sire_id, farmTenantId)
          : Promise.resolve(null);

        const [damData, sireData] = await Promise.all([damReq, sireReq]);
        if (abort) return;

        setDam(damData || null);
        setSire(sireData || null);
      } catch {
        if (abort) return;
        setDam(null);
        setSire(null);
      }
    };

    loadParents();
    return () => {
      abort = true;
    };
  }, [animal?.dam_id, animal?.sire_id, animal, farmTenantId]);

  const parentLabel = (p) =>
    p ? `${p.tag_number}${p.alias ? ` (${p.alias})` : ""}` : "—";

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    let cancelled = false;
    const loadTreatments = async () => {
      if (!id) return;
      setTreatmentsLoading(true);
      setTreatmentsError("");
      try {
        let page = 1;
        let pages = 1;
        const all = [];
        while (page <= pages) {
          const res = await getTreatmentsByAnimalId(
            id,
            { page, pageSize: 100 },
            farmTenantId
          );
          const items = Array.isArray(res?.items) ? res.items : [];
          all.push(...items);
          pages = Number(res?.pages || 1);
          page += 1;
          if (page > 200) break;
        }
        if (!cancelled) setTreatments(all);
      } catch (err) {
        if (!cancelled) {
          setTreatments([]);
          setTreatmentsError(err?.message || "Failed to load treatment records");
        }
      } finally {
        if (!cancelled) setTreatmentsLoading(false);
      }
    };
    loadTreatments();
    return () => {
      cancelled = true;
    };
  }, [id, farmTenantId]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setIsUploading(true);
    try {
      await uploadAnimalImages(id, files);
      const { images: newImages } = await getAnimalImages(id);
      setImages(newImages);
      enqueueSnackbar("Images uploaded successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to upload images", {
        variant: "error",
      });
    } finally {
      setIsUploading(false);
      event.target.value = ""; // Reset file input
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await deleteAnimalImage(imageId);
      setImages(images.filter((img) => img.id !== imageId));
      enqueueSnackbar("Image deleted successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to delete image", {
        variant: "error",
      });
    }
  };

  const openImageDialog = (image) => {
    setSelectedImage(image);
    setIsImageDialogOpen(true);
  };

  const closeImageDialog = () => {
    setIsImageDialogOpen(false);
    setSelectedImage(null);
  };

  const InfoRow = ({ label, value, tint = "success" }) => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{ py: 0.35, width: "100%", minWidth: 0 }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          width: { xs: 118, sm: 148 },
          maxWidth: { xs: 118, sm: 148 },
          flexShrink: 0,
          fontWeight: 600,
          letterSpacing: 0.02,
          bgcolor: (t) => {
            if (t.palette.mode === "dark") return "rgba(255,255,255,0.06)";
            return tint === "primary"
              ? "rgba(21, 101, 192, 0.1)"
              : "rgba(46, 125, 50, 0.1)";
          },
          px: 1,
          py: 0.75,
          borderRadius: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          pt: 0.25,
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </Typography>
    </Stack>
  );

  const formatMoney = (value, digits = 2) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(num);
  };

  const treatmentRows = useMemo(
    () =>
      [...treatments].sort((a, b) =>
        String(b?.date || "").localeCompare(String(a?.date || ""))
      ),
    [treatments]
  );

  const filteredTreatmentRows = useMemo(() => {
    const q = treatmentSearch.trim().toLowerCase();
    if (!q) return treatmentRows;
    return treatmentRows.filter((r) =>
      [
        r.treatment_id,
        r.treatment_of,
        r.medicine,
        r.brand,
        r.dosage,
        r.prescribed_by,
        r.dispensed_qty,
        r.dispensed_total_cost,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [treatmentRows, treatmentSearch]);

  const totalTreatmentCost = useMemo(
    () =>
      filteredTreatmentRows.reduce((sum, t) => {
        const c = Number(t.dispensed_total_cost);
        return sum + (Number.isFinite(c) ? c : 0);
      }, 0),
    [filteredTreatmentRows]
  );

  const costedRowsCount = useMemo(
    () =>
      filteredTreatmentRows.filter((t) => Number.isFinite(Number(t.dispensed_total_cost))).length,
    [filteredTreatmentRows]
  );

  const costCurrency = useMemo(
    () =>
      filteredTreatmentRows.find((t) => t.dispensed_currency)?.dispensed_currency || "UGX",
    [filteredTreatmentRows]
  );

  const medicineUsage = useMemo(() => {
    const m = new Map();
    filteredTreatmentRows.forEach((t) => {
      const key = t.medicine || "Unknown";
      m.set(key, (m.get(key) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredTreatmentRows]);

  const medicineSpend = useMemo(() => {
    const m = new Map();
    filteredTreatmentRows.forEach((t) => {
      const key = t.medicine || "Unknown";
      const cost = Number(t.dispensed_total_cost);
      if (!Number.isFinite(cost)) return;
      m.set(key, (m.get(key) || 0) + cost);
    });
    return Array.from(m.entries())
      .map(([name, total]) => ({ name, total: Number(total.toFixed(2)) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [filteredTreatmentRows]);

  const treatmentColumns = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        width: 120,
        valueGetter: (_, row) =>
          row?.date ? new Date(row.date).toLocaleDateString() : "—",
      },
      { field: "treatment_id", headerName: "Treatment ID", minWidth: 150, flex: 1 },
      { field: "treatment_of", headerName: "Condition", minWidth: 160, flex: 1 },
      { field: "medicine", headerName: "Medicine", minWidth: 150, flex: 1 },
      { field: "dosage", headerName: "Dosage", width: 120 },
      { field: "dispensed_qty", headerName: "Dispensed qty", width: 130 },
      {
        field: "dispensed_total_cost",
        headerName: "Cost",
        width: 130,
        valueGetter: (_, row) =>
          row?.dispensed_total_cost == null
            ? "—"
            : `${row?.dispensed_currency || costCurrency} ${formatMoney(row.dispensed_total_cost, 2)}`,
      },
      { field: "prescribed_by", headerName: "Prescribed by", minWidth: 150, flex: 1 },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        width: 130,
        renderCell: (p) => (
          <Button
            size="small"
            variant="outlined"
            component={RouterLink}
            to={`/treatment/${p.row.id}${profileQs}`}
          >
            View
          </Button>
        ),
      },
    ],
    [costCurrency, profileQs]
  );

  const canShowMilkingTab = useMemo(() => {
    const gender = String(animal?.gender || "").trim().toLowerCase();
    const category = String(animal?.category || "").trim().toLowerCase();
    const positiveGenderMarkers = ["female", "cow", "heifer"];
    const positiveCategoryMarkers = ["cow", "heifer", "dairy", "milking"];
    const negativeMarkers = ["male", "bull", "steer", "ox"];

    if (
      positiveGenderMarkers.some((token) => gender.includes(token)) ||
      positiveCategoryMarkers.some((token) => category.includes(token))
    ) {
      return true;
    }

    if (
      negativeMarkers.some((token) => gender.includes(token)) ||
      negativeMarkers.some((token) => category.includes(token))
    ) {
      return false;
    }

    return false;
  }, [animal?.gender, animal?.category]);

  useEffect(() => {
    if (!canShowMilkingTab && activeTab === TAB_MILKING) {
      setActiveTab(TAB_OVERVIEW);
    }
  }, [canShowMilkingTab, activeTab]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!animal) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Animal not found</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: 4,
        pt: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 1280, lg: 1440 },
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Breadcrumbs
          sx={{ mb: 2, flexWrap: "wrap" }}
          aria-label="breadcrumb"
        >
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={APP_HOME_PATH}
          >
            Dashboard
          </Link>
          <Link
            component={RouterLink}
            underline="hover"
            color="inherit"
            to={animalsListHref}
          >
            Animals
          </Link>
          <Typography color="text.primary">
            {animal.alias || animal.tag_number}
          </Typography>
        </Breadcrumbs>

        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: 1,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ minWidth: 0, flex: { sm: "1 1 auto" } }}
            >
              <Box
                sx={{
                  width: { xs: 48, sm: 52 },
                  height: { xs: 48, sm: 52 },
                  flexShrink: 0,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "success.main",
                  color: "success.contrastText",
                  boxShadow: 1,
                }}
              >
                <LivestockIcon sx={{ fontSize: { xs: 28, sm: 30 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: -0.5,
                    color: "text.primary",
                    lineHeight: 1.2,
                    fontSize: { xs: "1.35rem", sm: "2.125rem" },
                  }}
                >
                  {animal.alias || "Unnamed animal"}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mt: 0.75, gap: 0.75 }}
                >
                  <Chip
                    size="small"
                    label={`Tag ${animal.tag_number}`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: "rgba(21, 101, 192, 0.12)",
                      color: "primary.dark",
                    }}
                  />
                  {animal.category ? (
                    <Chip
                      size="small"
                      label={animal.category}
                      sx={{
                        fontWeight: 600,
                        bgcolor: "rgba(237, 108, 2, 0.14)",
                        color: "warning.dark",
                      }}
                    />
                  ) : null}
                  {animal.gender ? (
                    <Chip
                      size="small"
                      label={animal.gender}
                      variant="outlined"
                      sx={{ borderColor: "success.main", color: "success.dark" }}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{
                width: { xs: "100%", sm: "auto" },
                alignSelf: { xs: "stretch", sm: "auto" },
                minWidth: 0,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<ArrowBackIcon />}
                component={RouterLink}
                to={animalsListHref}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  width: { xs: "100%", sm: "auto" },
                  boxShadow: "0 4px 12px rgba(21, 101, 192, 0.28)",
                }}
              >
                Animal list
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                component={RouterLink}
                to={editAnimalHref}
                sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
              >
                Edit animal
              </Button>
              <Button
                variant="outlined"
                color="success"
                component={RouterLink}
                to={createAnimalHref}
                sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
              >
                Add animal
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={() => setActiveTab(TAB_TREATMENTS)}
                sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
              >
                Treatment records
              </Button>
              {canShowMilkingTab ? (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setActiveTab(TAB_MILKING)}
                  sx={{ borderRadius: 2, width: { xs: "100%", sm: "auto" } }}
                >
                  Milking records
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={narrowTabs ? "scrollable" : "fullWidth"}
            scrollButtons={narrowTabs ? "auto" : false}
            allowScrollButtonsMobile
            sx={{
              px: 1,
              pt: 1,
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "none",
                fontSize: "1rem",
                minHeight: 52,
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                bgcolor: "primary.main",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Family tree" />
            <Tab label="Treatment records" />
            {canShowMilkingTab ? <Tab label="Milking" /> : null}
          </Tabs>
        </Paper>
        {activeTab === TAB_OVERVIEW && (
          <>
            {/* True 50/50 split: CSS Grid avoids MUI v7 Grid legacy `item`/breakpoint quirks */}
            <Box
              sx={{
                display: "grid",
                width: "100%",
                boxSizing: "border-box",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "minmax(0, 1fr) minmax(0, 1fr)",
                },
                alignItems: "stretch",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  minWidth: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { md: 320 },
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(46, 125, 50, 0.22)",
                  borderLeft: "5px solid",
                  borderLeftColor: "success.main",
                  bgcolor: "background.paper",
                  boxShadow: "0 6px 24px rgba(46, 125, 50, 0.07)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{ color: "success.dark", letterSpacing: 0.2 }}
                >
                  Basic information
                </Typography>
                <Divider sx={{ my: 2, opacity: 0.85 }} />
                <Stack spacing={1.25} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                  <InfoRow label="Tag Number" value={animal?.tag_number} />
                  <InfoRow label="Alias" value={animal?.alias} />
                  <InfoRow label="Breed" value={animal?.breed} />
                  <InfoRow
                    label="Pattern / Markings"
                    value={animal?.pattern_markings}
                  />
                  <InfoRow label="Gender" value={animal?.gender} />
                  <InfoRow label="Category" value={animal?.category} />
                  <InfoRow
                    label="Date of Birth"
                    value={
                      animal?.dob
                        ? new Date(animal.dob).toISOString().slice(0, 10)
                        : "—"
                    }
                  />
                  <InfoRow
                    label="Status"
                    value={
                      <Chip
                        size="small"
                        label={animal?.status ?? "—"}
                        {...statusChipSx(animal?.status)}
                      />
                    }
                  />
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  minWidth: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: { md: 320 },
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "rgba(21, 101, 192, 0.22)",
                  borderLeft: "5px solid",
                  borderLeftColor: "primary.main",
                  bgcolor: "background.paper",
                  boxShadow: "0 6px 24px rgba(21, 101, 192, 0.07)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{ color: "primary.dark", letterSpacing: 0.2 }}
                >
                  Additional details
                </Typography>
                <Divider sx={{ my: 2, opacity: 0.85 }} />
                <Stack spacing={1.25} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                  <InfoRow
                    tint="primary"
                    label="Weight"
                    value={animal?.weight ? `${animal.weight} kg` : "—"}
                  />
                  <InfoRow
                    tint="primary"
                    label="Active"
                    value={animal?.is_active ? "Yes" : "No"}
                  />
                  <InfoRow
                    tint="primary"
                    label="Kraal"
                    value={animal?.kraal_name}
                  />
                  <InfoRow
                    tint="primary"
                    label="Dam"
                    value={parentLabel(dam)}
                  />
                  <InfoRow
                    tint="primary"
                    label="Sire"
                    value={parentLabel(sire)}
                  />

                  <InfoRow
                    tint="primary"
                    label="Tags"
                    value={
                      animal?.tags?.length ? (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                        >
                          {animal.tags.map((t) => (
                            <Chip
                              key={t}
                              size="small"
                              label={t}
                              variant="outlined"
                              sx={{
                                borderColor: "secondary.main",
                                color: "secondary.dark",
                                bgcolor: "rgba(156, 39, 176, 0.06)",
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <InfoRow
                    tint="primary"
                    label="Description"
                    value={animal?.description}
                  />
                </Stack>
              </Paper>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "rgba(237, 108, 2, 0.28)",
                borderLeft: "5px solid",
                borderLeftColor: "warning.main",
                bgcolor: "background.paper",
                boxShadow: "0 6px 24px rgba(237, 108, 2, 0.08)",
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ color: "warning.dark", letterSpacing: 0.2 }}
              >
                Animal images
              </Typography>
              <Divider sx={{ my: 2, opacity: 0.85 }} />

              <Box sx={{ mb: 3 }}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="animal-image-upload"
                  type="file"
                  multiple
                  onChange={handleImageUpload}
                />
                <label htmlFor="animal-image-upload">
                  <Button
                    variant="contained"
                    component="span"
                    color="warning"
                    startIcon={<CloudUploadIcon />}
                    disabled={isUploading}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700,
                      boxShadow: "0 4px 14px rgba(237, 108, 2, 0.35)",
                    }}
                  >
                    {isUploading ? "Uploading…" : "Upload images"}
                  </Button>
                </label>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.5, maxWidth: 480 }}
                >
                  Add multiple photos — useful for identification and records.
                </Typography>
              </Box>

              {images.length > 0 ? (
                <Grid container spacing={2}>
                  {images.map((image) => (
                    <Grid item xs={6} sm={4} md={3} key={image.id}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="140"
                          image={apiUrl(`uploads/${image.filename}`)}
                          alt={image.filename}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/placeholder-image.jpg";
                          }}
                          onClick={() => openImageDialog(image)}
                          sx={{
                            cursor: "pointer",
                            objectFit: "cover",
                          }}
                        />
                      <CardActions
                        sx={{
                          justifyContent: "space-between",
                          padding: "8px",
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ maxWidth: "70%" }}
                        >
                          {new Date(image.uploaded_at).toLocaleDateString()}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    py: 3,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(237, 108, 2, 0.06)",
                    border: "1px dashed",
                    borderColor: "warning.light",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No images yet — upload photos above when you have them.
                  </Typography>
                </Box>
              )}
            </Paper>
          </>
        )}
        {activeTab === TAB_FAMILY && (
          <AnimalFamilyTree animalId={animal.id} farmTenantId={farmTenantId} />
        )}
        {activeTab === TAB_TREATMENTS && (
          <Box>
            {treatmentsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {treatmentsError}
              </Alert>
            )}
            {treatmentsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress />
              </Box>
            )}
            {!treatmentsLoading && !treatmentsError && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" color="text.secondary">Total treatments</Typography>
                    <Typography variant="h5">{filteredTreatmentRows.length}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" color="text.secondary">Medicines used</Typography>
                    <Typography variant="h5">{medicineUsage.length}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" color="text.secondary">Costed records</Typography>
                    <Typography variant="h5">{costedRowsCount}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography variant="overline" color="text.secondary">Total medication cost</Typography>
                    <Typography variant="h6">{costCurrency} {formatMoney(totalTreatmentCost, 2)}</Typography>
                  </Paper>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0, 1fr))",
                    },
                  }}
                >
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", height: 340 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                      Medicine usage count
                    </Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <BarChart data={medicineUsage}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" interval={0} angle={-20} textAnchor="end" height={60} />
                        <YAxis />
                        <ReTooltip />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {medicineUsage.map((_, idx) => (
                            <Cell key={`bar-cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>

                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", height: 340 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                      Cost share by medicine
                    </Typography>
                    {medicineSpend.length > 0 ? (
                      <ResponsiveContainer width="100%" height="90%">
                        <PieChart>
                          <Pie data={medicineSpend} dataKey="total" nameKey="name" outerRadius={105} label>
                            {medicineSpend.map((_, idx) => (
                              <Cell key={`cost-cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Legend />
                          <ReTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No medication cost snapshots recorded for this animal yet.
                      </Alert>
                    )}
                  </Paper>
                </Box>

                <Paper elevation={0} sx={{ mt: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                  <Box sx={{ p: 2, pb: 0 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Treatment records
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        component={RouterLink}
                        to={addTreatmentHref}
                        startIcon={<AddIcon />}
                        sx={{
                          borderRadius: 2,
                          alignSelf: { xs: "stretch", sm: "auto" },
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(21, 101, 192, 0.28)",
                        }}
                      >
                        Add Treatment
                      </Button>
                    </Stack>
                    <Box sx={{ mt: 1.5, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Search treatments"
                        placeholder="Search by treatment ID, condition, medicine, dosage, prescriber..."
                        value={treatmentSearch}
                        onChange={(e) => setTreatmentSearch(e.target.value)}
                      />
                    </Box>
                  </Box>
                  {isMobileList ? (
                    <Stack spacing={1.5} sx={{ p: 2 }}>
                      {filteredTreatmentRows.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                          No treatment records found.
                        </Typography>
                      ) : (
                        filteredTreatmentRows.map((row) => (
                          <MobileListCard
                            key={row.id}
                            title={row.treatment_id || `Treatment #${row.id}`}
                            subtitle={
                              row?.date
                                ? new Date(row.date).toLocaleDateString()
                                : "—"
                            }
                            fields={[
                              { label: "Condition", value: row.treatment_of || "—" },
                              { label: "Medicine", value: row.medicine || "—" },
                              { label: "Dosage", value: row.dosage || "—" },
                              {
                                label: "Cost",
                                value:
                                  row?.dispensed_total_cost == null
                                    ? "—"
                                    : `${row?.dispensed_currency || costCurrency} ${formatMoney(row.dispensed_total_cost, 2)}`,
                              },
                            ]}
                            actions={
                              <Button
                                size="small"
                                variant="outlined"
                                component={RouterLink}
                                to={`/treatment/${row.id}${profileQs}`}
                              >
                                View
                              </Button>
                            }
                          />
                        ))
                      )}
                    </Stack>
                  ) : (
                  <DataGrid
                    rows={filteredTreatmentRows}
                    columns={treatmentColumns}
                    getRowId={(r) => r.id}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        printOptions: { disableToolbarButton: true },
                      },
                    }}
                    initialState={{
                      pagination: { paginationModel: { page: 0, pageSize: 10 } },
                    }}
                    disableRowSelectionOnClick
                    sx={{ border: "none" }}
                  />
                  )}
                </Paper>
              </>
            )}
          </Box>
        )}
        {canShowMilkingTab && activeTab === TAB_MILKING && <AnimalMilking animalId={animal.id} />}
      </Box>
      {/* {activeTab === 4 && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6">Health Records Coming Soon</Typography>
        </Paper>
      )} */}
      {/* Image Preview Dialog */}
      <Dialog
        open={isImageDialogOpen}
        onClose={closeImageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {selectedImage?.filename}
            <IconButton onClick={closeImageDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <img
                src={apiUrl(selectedImage.filepath)}
                alt={selectedImage.filename}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnimalProfile;
